import * as React from 'react';
import { Button, Modal, UIHeading1 } from '@cian/ui-kit';

import { FieldRow, MoreFiltersModalContentLayout } from '../../../components/Filters';
import { FiltersFooterLayout } from '../../../components/Filters/FiltersFooterLayout';
import { BannerLoader } from '../../../components/Filters/BannerLoader';
import { FilterAreaWrapper } from '../../../components/Filters/FilterAreaWrapper';
import { FiltersBannerWrapper } from '../../../components/Filters/FiltersBannerWrapper';
import { NewbuildingCompilationFilterWrapper } from '../../../components/Filters/NewbuildingCompilationFilterWrapper';
import { ApartmentTypeFilterWrapper } from '../../../components/Filters/ApartmentTypeFilterWrapper';
import { FiltersContentWrapper } from '../../../components/Filters/FiltersContentWrapper';
import { SearchButton } from '../../../components/SearchButton';
import { NO_BREAK_SPACE } from '../../../constants/symbolsUnicode';
import { ADFOX_TIMEOUT } from '../../../constants/adfox';
import {
  ManifestationType,
  TotalAreaInput,
  KitchenAreaInput,
  LivingAreaInput,
  ApartmentType,
  HouseType,
  NewbuildingClass,
  UndergroundWalkTimeInput,
  HasDecoration,
  ConstructionStatus,
  IsSalesStart,
  IsSalesLeader,
  HasDiscountMortgage,
  DiscountPromoSearchType,
  MortgagePromoSearchType,
} from '../../Filters';
import { AdfoxBannerAdditionalFilters } from '../../../components/AdfoxBanner';
import { FiltersGroup } from '../../../components/Filters/FiltersGroup';

export interface IModalStoreProps {
  count: number | null;
  isCountFetching?: boolean;
  hasUndreground: boolean;
  canUseHiddenBase: boolean;
  discountMortgageEnabled: boolean;
  promoSearchTypesLoaded: boolean;
}

export interface IModalDispatchProps {
  onSubmit(canUseHiddenBase: boolean): void;
  trackingPopupMoreParamOpen(): void;
  updateTags(): void;
  onClearFilters(): void;
}

type TModalProps = IModalStoreProps & IModalDispatchProps;

export const MoreFiltersModalComponent: React.FC<TModalProps> = React.memo(
  ({
    count,
    isCountFetching,
    hasUndreground,
    discountMortgageEnabled,
    promoSearchTypesLoaded,
    canUseHiddenBase,
    updateTags,
    onSubmit,
    trackingPopupMoreParamOpen,
    onClearFilters,
  }) => {
    const [isOpen, setOpen] = React.useState(false);

    const [bannerLoaded, setBannerLoaded] = React.useState(false);
    const [bannerError, setBannerError] = React.useState(false);

    const adTimer = React.useRef<number>();

    React.useEffect(
      () => () => {
        if (adTimer.current) {
          window.clearTimeout(adTimer.current);
        }
      },
      [adTimer],
    );

    const handleAdfoxMount = React.useCallback(() => {
      if (adTimer.current) {
        window.clearTimeout(adTimer.current);
      }

      setBannerLoaded(true);
      setBannerError(false);
    }, [adTimer]);

    const handleAdfoxError = React.useCallback(() => {
      setBannerLoaded(true);
      setBannerError(true);
    }, []);

    const handleToggle = React.useCallback(
      (isOpen: boolean) => {
        if (isOpen) {
          trackingPopupMoreParamOpen();

          adTimer.current = window.setTimeout(handleAdfoxError, ADFOX_TIMEOUT);
        } else {
          updateTags(); // При закрытии модального окна обновляем тэги
        }

        setOpen(isOpen);
        setBannerLoaded(false);
        setBannerError(false);
      },
      [adTimer, handleAdfoxError, trackingPopupMoreParamOpen, updateTags],
    );

    const handleSubmit = React.useCallback(() => {
      onSubmit(canUseHiddenBase);

      setOpen(false);
      setBannerLoaded(false);
      setBannerError(false);
    }, [onSubmit, canUseHiddenBase]);

    const handleClose = React.useCallback(() => {
      handleToggle(false);
    }, [handleToggle]);

    const handleClear = React.useCallback(() => {
      onClearFilters();
    }, [onClearFilters]);

    return (
      <>
        <Button size="XS" tabIndex={-1} theme="stroke_secondary" type="button" onClick={() => handleToggle(true)}>
          Ещё фильтры
        </Button>
        <Modal
          size="L"
          open={Boolean(isOpen)}
          onOpenChange={handleClose}
          header={<UIHeading1>Все фильтры</UIHeading1>}
          footer={
            <FiltersFooterLayout>
              <Button
                size="M"
                theme="fill_secondary"
                type="button"
                onClick={handleClear}
                data-testid="ClearFiltersButton"
              >
                Сбросить всё
              </Button>
              <SearchButton count={count} loading={isCountFetching} size="M" onClick={handleSubmit} />
            </FiltersFooterLayout>
          }
          body={
            <MoreFiltersModalContentLayout>
              {!bannerLoaded && <BannerLoader />}
              <FiltersContentWrapper isVisible={bannerLoaded}>
                {!bannerError && (
                  <FiltersBannerWrapper>
                    <AdfoxBannerAdditionalFilters onError={handleAdfoxError} onLoad={handleAdfoxMount} />
                  </FiltersBannerWrapper>
                )}

                <FiltersGroup title="О квартире">
                  <FieldRow
                    title="Площадь"
                    field={
                      <FilterAreaWrapper
                        totalArea={<TotalAreaInput />}
                        kitchenArea={<KitchenAreaInput />}
                        livingArea={<LivingAreaInput />}
                      />
                    }
                  />

                  <FieldRow title="Отделка" field={<HasDecoration />} dataTestId="DecorationRow" />

                  <FieldRow
                    title="Апартаменты"
                    field={<ApartmentTypeFilterWrapper apartmentType={<ApartmentType />} />}
                    dataTestId="ApartmentTypeRow"
                  />
                </FiltersGroup>

                <FiltersGroup title="О жилом комплексе и корпусе">
                  <FieldRow title="Материал дома" field={<HouseType />} dataTestId="HouseTypeRow" />
                  <FieldRow title="Класс жилья" field={<NewbuildingClass />} dataTestId="NewbuildingClassRow" />
                  <FieldRow title="Кто продаёт" field={<ManifestationType />} dataTestId="ManifestationTypeRow" />

                  {hasUndreground && (
                    <FieldRow
                      title="Пешком до метро"
                      field={<UndergroundWalkTimeInput />}
                      dataTestId="UndergroundWalkTimeRow"
                    />
                  )}

                  <FieldRow
                    title="Статус строительства"
                    field={<ConstructionStatus />}
                    dataTestId="ConstructionStatusRow"
                  />
                </FiltersGroup>

                <FiltersGroup title="Выгодно">
                  {promoSearchTypesLoaded && (
                    <>
                      <FieldRow
                        title="Ипотечные программы"
                        field={<MortgagePromoSearchType />}
                        dataTestId="advancedFilter_mortgagePromoSearchType"
                      />
                      <FieldRow
                        title={`Скидки и акции от${NO_BREAK_SPACE}застройщиков`}
                        field={<DiscountPromoSearchType />}
                        dataTestId="advancedFilter_discountPromoSearchType"
                      />
                    </>
                  )}
                  <FieldRow
                    title="Подборки жилых комплексов"
                    field={
                      <NewbuildingCompilationFilterWrapper
                        salesStart={<IsSalesStart />}
                        salesLeader={<IsSalesLeader />}
                        discountMortgage={discountMortgageEnabled ? <HasDiscountMortgage /> : undefined}
                      />
                    }
                    dataTestId="NewbuildingCompliationRow"
                  />
                </FiltersGroup>
              </FiltersContentWrapper>
            </MoreFiltersModalContentLayout>
          }
        />
      </>
    );
  },
);

MoreFiltersModalComponent.displayName = 'MoreFiltersModalComponent';
